/**
 * Backend version tracking.
 *
 * Fetched once at app startup from lobby `/api/status` and cached for
 * the lifetime of the page. Used in console logs and error reports so
 * users can paste the version string into bug reports.
 */

import { useSyncExternalStore } from 'react';

const UNKNOWN = 'unknown';

let cached: string = UNKNOWN;
const listeners = new Set<() => void>();

export function getBackendVersion(): string {
  return cached;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * React hook for subscribing to the backend version. Returns the live cached
 * value and triggers a re-render once the startup fetch resolves.
 */
export function useBackendVersion(): string {
  return useSyncExternalStore(subscribe, getBackendVersion, getBackendVersion);
}

function getLobbyBaseUrl(): string {
  return import.meta.env.VITE_LOBBY_SERVER_URL || '';
}

export async function fetchBackendVersion(): Promise<void> {
  const base = getLobbyBaseUrl();
  const url = `${base}/api/status`;

  let fetched: string | null = null;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      console.warn(`[polymyth] version fetch failed: HTTP ${response.status}`);
      return;
    }
    const data = (await response.json()) as { version?: unknown };
    if (typeof data.version === 'string' && data.version.length > 0) {
      fetched = data.version;
    } else {
      console.warn('[polymyth] version fetch: missing version field');
    }
  } catch (err) {
    console.warn('[polymyth] version fetch failed:', err);
  }

  if (fetched !== null) {
    cached = fetched;
    // Notify outside the fetch try/catch so a throwing listener doesn't
    // masquerade as a network error, and isolate each listener so one
    // throw doesn't silence the rest.
    listeners.forEach((l) => {
      try {
        l();
      } catch (listenerErr) {
        console.warn('[polymyth] version listener threw:', listenerErr);
      }
    });
  }
}
